/**
 * EvaluateProperty — the deep-linkable Per-Property Evaluation Workspace route
 * (spec §3.1). Registered INSIDE the roles={['admin','staff']} → <AppShell/> block
 * so the locked sidebar + sticky top-chrome persist and this workspace is the
 * route body.
 *
 *   /evaluate/:clientId/:projectId/:propertyId?floorId=<FloorId>
 *
 * STABLE IDs ONLY — keyed on the property (P#####) + project (VP###); no candidate
 * id appears anywhere. The deep link RESOLVES THROUGH `linkPropertyAsCandidate`
 * (the ONE unified, idempotent link path) BEFORE the marking surface renders, so
 * the candidate slot is present + stamped first (spec §2.5 deep-link rule).
 *
 * Tier resolution reuses the project's tierCode → `${tierCode}_R1` intake doc (the
 * panel's existing L1A check keys off this — no hardcoded tier guard).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { listenProjects, type LiveProject } from '../lib/projectStore';
import { getProperty } from '../lib/propertyStore';
import {
  activeConcept,
  activeVersion,
  versionFloors,
  type LiveProperty,
} from '../lib/propertyModel';
import { resolvePropertyGridForMarking } from '../lib/propertyGridForMarking';
import { linkPropertyAsCandidate } from '../lib/linkPropertyAsCandidate';
import { usePageChrome, type BreadcrumbSegment } from '../lib/pageChromeContext';
import type { PropertyRefLike } from '../lib/candidatePropertyJoin';
import PropertyEvaluationWorkspace, {
  type FloorChoice,
} from '../components/evaluate/PropertyEvaluationWorkspace';
import '../styles/evaluate-workspace.css';

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--s-6) var(--s-4)',
        margin: 'var(--s-4)',
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface)',
        color: 'var(--ink-soft)',
        fontSize: 'var(--fs-sec)',
        lineHeight: 'var(--lh-body)',
      }}
    >
      {children}
    </div>
  );
}

export default function EvaluateProperty() {
  const { clientId = '', projectId = '', propertyId = '' } = useParams<{
    clientId: string;
    projectId: string;
    propertyId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const floorId = searchParams.get('floorId');
  const navigate = useNavigate();
  const { role, user } = useAuth();
  const isAdmin = role === 'admin';
  const actorName = user?.displayName || user?.email || 'Staff';

  // ── the project (tier · refs · names) — live, find by pid ──
  const [projects, setProjects] = useState<LiveProject[]>([]);
  const [projectsReady, setProjectsReady] = useState(false);
  useEffect(() => {
    const unsub = listenProjects(setProjects, {
      onReady: () => setProjectsReady(true),
      onErr: () => setProjectsReady(true),
    });
    return unsub;
  }, []);
  const project = useMemo(
    () => projects.find((p) => p.pid === projectId) ?? null,
    [projects, projectId],
  );
  const intakeTier = project ? `${project.tierCode}_R1` : undefined;
  const linkedPropertyRefs: PropertyRefLike[] = useMemo(
    () => (project?.propertyRefs ?? []).map((r) => ({ propertyId: r.propertyId })),
    [project],
  );

  // ── ensure the candidate slot exists BEFORE the marking surface renders ──
  // (deep-link rule, spec §2.5). Idempotent; runs once per (project, property).
  const [candidateReady, setCandidateReady] = useState(false);
  const linkSigRef = useRef('');
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!project || !projectId || !propertyId || !intakeTier) return;
    const sig = `${projectId}::${propertyId}::${intakeTier}`;
    // Same target already handled (in-flight or done) → don't restart the link
    // work AND don't reset candidateReady. Readiness is tied to the SIGNATURE
    // below, not to this effect instance, so a StrictMode double-mount or a
    // project-snapshot re-render (listenProjects mints a fresh project object on
    // every snapshot → linkedPropertyRefs churns) can no longer strand
    // candidateReady=false. That stranding was the cold deep-link hang on
    // "Resolving the property and its grid…" (2026-07-01): the first run was torn
    // down (alive=false) mid-flight, the re-run short-circuited on this guard, and
    // the original finally then skipped setCandidateReady(true) forever.
    if (linkSigRef.current === sig) return;
    linkSigRef.current = sig;
    setCandidateReady(false);
    (async () => {
      try {
        await linkPropertyAsCandidate(
          projectId,
          clientId,
          propertyId,
          'candidate',
          intakeTier,
          { refs: linkedPropertyRefs },
        );
      } catch {
        /* a transient link failure must not dead-end the surface — the panel's
           own auto-join is the backstop; we still render. */
      } finally {
        // Flip ready as long as (a) the component is still mounted and (b) we're
        // still resolving THIS target. A later navigation to another property
        // moves linkSigRef on, so a stale completion correctly no-ops instead of
        // marking the wrong property ready.
        if (mountedRef.current && linkSigRef.current === sig) {
          setCandidateReady(true);
        }
      }
    })();
  }, [project, projectId, propertyId, intakeTier, clientId, linkedPropertyRefs]);

  // ── the property (title · address · floors) ──
  const [property, setProperty] = useState<LiveProperty | null>(null);
  const [propLoaded, setPropLoaded] = useState(false);
  useEffect(() => {
    if (!propertyId) return;
    let alive = true;
    setPropLoaded(false);
    (async () => {
      try {
        const p = await getProperty(propertyId);
        if (alive) setProperty(p);
      } catch {
        if (alive) setProperty(null);
      } finally {
        if (alive) setPropLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [propertyId]);

  const floors: FloorChoice[] = useMemo(() => {
    if (!property) return [];
    const concept = activeConcept(property);
    const version = concept ? activeVersion(concept) : null;
    const fls = version ? versionFloors(version) : [];
    return fls.map((f) => ({ floorId: f.floorId, label: f.label || f.floorId }));
  }, [property]);

  const { title, address } = useMemo(() => {
    const base =
      property?.address ||
      property?.addressComps?.street ||
      property?.addressComps?.city ||
      `Property ${propertyId}`;
    const grid = property ? resolvePropertyGridForMarking(property) : null;
    const floorLabel =
      floors.find((f) => f.floorId === floorId)?.label || grid?.floorLabel || '';
    return {
      title: floorLabel ? `${base} — ${floorLabel}` : base,
      address: property?.address || '',
    };
  }, [property, propertyId, floors, floorId]);

  // ── breadcrumb into the shared top chrome (Vera polish) — reuse the SAME
  // pageChromeContext Pass 1 uses; reads "Clients › {client} › {VP###} ›
  // {property}", segments link back to the contact + the project. The in-workspace
  // identity header is unchanged. Called every render (hook rule) so it republishes
  // as the project/property resolve. ── */
  const breadcrumb = useMemo<BreadcrumbSegment[]>(() => {
    const segs: BreadcrumbSegment[] = [{ label: 'Clients', href: '/contacts?role=clients' }];
    const cName = project?.clientName?.trim();
    if (cName) {
      segs.push(clientId ? { label: cName, href: `/contacts/${clientId}` } : { label: cName });
    }
    if (project) {
      segs.push({ label: project.vp || project.projectName || projectId, href: `/project/${projectId}` });
    }
    segs.push({ label: title || `Property ${propertyId}` });
    return segs;
  }, [project, clientId, projectId, propertyId, title]);
  usePageChrome({ breadcrumb, title });

  // ── guards ──
  if (!projectId || !propertyId) {
    return <Notice>This evaluation link is missing its project or property id.</Notice>;
  }
  if (!projectsReady || !project) {
    return projectsReady && !project ? (
      <Notice>
        Project <b>{projectId}</b> could not be found (it may have been moved or you
        may not have access).
      </Notice>
    ) : (
      <Notice>Preparing the evaluation workspace…</Notice>
    );
  }
  if (!propLoaded || !candidateReady) {
    return <Notice>Resolving the property and its grid…</Notice>;
  }
  if (!property) {
    return (
      <Notice>
        Property <b>{propertyId}</b> could not be loaded. Open it from the project's
        Linked Properties and try again.
      </Notice>
    );
  }

  return (
    <PropertyEvaluationWorkspace
      clientId={clientId || project.clientId || null}
      projectId={projectId}
      propertyId={propertyId}
      floorId={floorId}
      title={title}
      address={address}
      projectName={project.projectName}
      clientName={project.clientName}
      floors={floors}
      actor={{ uid: user?.uid ?? null, name: actorName }}
      isAdmin={isAdmin}
      intakeTier={intakeTier}
      linkedPropertyRefs={linkedPropertyRefs}
      onFloorChange={(fid) => {
        const next = new URLSearchParams(searchParams);
        next.set('floorId', fid);
        setSearchParams(next, { replace: true });
      }}
      onDone={() => navigate(`/project/${projectId}`)}
    />
  );
}
